import { Head, Link } from "@inertiajs/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface Region {
  id: number;
  name: string;
  sort?: number | null;
  status?: string | null;
  created_at: string;
  updated_at: string;
  parent_id?: number | null;
}

interface Props {
  region: Region;
  parent: Region | null;
  children: Region[];
}

export default function Show({ region, parent, children }: Props) {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("ar-UK", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const infoItems = [
    { label: "اسم المنطقة", value: region.name },
    { label: "الترتيب", value: region.sort?.toString() || "—" },
    { label: "الحالة", value: region.status || "—" },
    { label: "المنطقة الأم", value: parent?.name || "—" },
    { label: "عدد المناطق الفرعية", value: children.length.toString() },
    { label: "تاريخ الإنشاء", value: formatDate(region.created_at) },
    { label: "تاريخ التحديث", value: formatDate(region.updated_at) },
  ];

  return (
    <>
      <Head title={`تفاصيل المنطقة`} />

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>تفاصيل المنطقة</CardTitle>
            <CardDescription>معلومات المنطقة الأساسية</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link href="/desk/regions">عودة للقائمة</Link>
            </Button>
            <Button asChild>
              <Link href={`/desk/regions/${region.id}/edit`}>تعديل المنطقة</Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            {infoItems.map((item) => (
              <div key={item.label} className="rounded-lg border bg-muted/40 p-4">
                <p className="text-xs font-medium text-muted-foreground">{item.label}</p>
                <div className="mt-2 text-sm text-foreground">{item.value}</div>
              </div>
            ))}
          </div>

          {children.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-4">المناطق الفرعية</h3>
              <div className="rounded-lg border bg-muted/40 p-4">
                <div className="space-y-2">
                  {children.map((child) => (
                    <div
                      key={child.id}
                      className="flex items-center justify-between rounded-md border bg-background p-3"
                    >
                      <div>
                        <p className="font-medium">{child.name}</p>
                        {child.sort !== null && child.sort !== undefined && (
                          <p className="text-xs text-muted-foreground">ترتيب: {child.sort}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" asChild>
                          <Link href={`/desk/regions/${child.id}`}>عرض</Link>
                        </Button>
                        <Button variant="ghost" size="sm" asChild>
                          <Link href={`/desk/regions/${child.id}/edit`}>تعديل</Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
